<template>
  <div class="container">
    <p>
      As part of the 16th international meeting on 'Visualizing Biological Data', we're launching
      <b>VIZBI <i>Online</i></b
      >, a new online event series (<router-link to="/2026#faq">see FAQ</router-link>) kicking off
      with a
      <router-link to="/2026/Masterclass">Masterclass</router-link> on <b>{{ masterclass }}</b> and
      a <router-link :to="`/2026/Program#${workshopDayHash}`">Workshop</router-link> on
      <b>{{ workshop }}</b
      >, running each day {{ hoursRange }}
      (<TimeZoneInline />).
      Both events build on the in-person VIZBI 2026 meeting held earlier this year at Cambridge
      University, UK. When you register, you gain immediate access to the
      <router-link :to="{path: $route.path, hash: '#faq&videos'}"
      >corresponding recorded presentations</router-link
      >
      from the Cambridge meeting, along with videos from previous VIZBI events.
    </p>

    <HeroVideoVimeo
      v-if="$route.path.match(/\/2026/)"
      id="1145497607"
      credit="Leonora Martínez-Nuñez" />

    <!-- stable anchor: /2026#Faraday -->
    <p id="Faraday">
      VIZBI <i>Online</i> uses a
      <!-- to the sidebar's own answers (Seán, 2026-10-22); the path as it is, so the page stays -->
      <router-link :to="{path: $route.path, hash: '#faq&format'}">new format</router-link>
      inspired by the
      <router-link :to="{path: $route.path, hash: '#faq&faraday'}">Faraday Discussions</router-link
      >. Participants watch speakers' recorded talks in advance, then join live sessions with
      five-minute speaker summaries, allowing more time for deeper, more focused discussions with
      speakers and peers.
    </p>
    <p>
      Each day begins with 5-minute summaries from each speaker, highlighting key ideas and updates
      since their Cambridge presentation. We then move into breakout discussions, providing an
      opportunity to engage directly with speakers and other participants. This is a chance to ask
      questions, exchange ideas, and get feedback on your own work from world-leading experts and
      <a href="/one-pager.pdf">VIZBI's interdisciplinary community</a>.
    </p>

    <p>
      If you attended VIZBI 2026 in April, you can join the corresponding November events
      <router-link :to="{path: $route.path, hash: '#faq&cambridge'}"
      >free of charge</router-link
      ><template v-if="isRegisteredForAll">.</template
      ><template v-else>—but you still need to register to reserve your place.</template>
      <!-- the 2-for-1 deal, for everyone (a registered reader may be asked by a colleague); as
           #register's "?" and the Registration page, in brief (2026-10-14) -->
      Otherwise, we're offering a
      <router-link to="/2026/Registration#two-for-one"><b>2-for-1 deal</b></router-link> that lets
      you and a colleague attend the same
      events for the price of one registration.
    </p>
    <p>
      Registration fees help sustain the in-person VIZBI meetings that make VIZBI <i>Online</i>
      possible. VIZBI is run entirely by unpaid volunteers, with finances managed by the
      International Society for Computational Biology (<a href="https://www.iscb.org">ISCB</a>), a
      registered non-profit.
    </p>

    <!-- registered for both events: no button (the banner counts down); for one: the other, in
         the banner's wording -->
    <RegisterNowWrapper
      v-if="!isRegisteredForAll"
      is-always-shown
      class="isFullWidth"
      :for-event="unregisteredEventKey"
      :label="registerLabel" />

    <!-- the mailing list, under the button (2026-10-14): signed out, sign up; signed in without
         it, one click opens Profile and switches it on in view; already on it, nothing to say -->
    <p v-if="!isSignedIn" class="mailingList">
      If you're not ready to register yet,
      <router-link :to="{hash: '#sign-in'}">sign up</router-link> to receive email updates.
    </p>
    <p v-else-if="!isSubscribed" class="mailingList">
      You can
      <a href="#profile" @click.prevent="openSubscribe">update your preferences</a> to receive email
      updates.
    </p>

    <KeyDates :items="upcomingKeyDates">
      <template #headerRight><TimeZoneInline variant="compact" /></template>
    </KeyDates>
  </div>
</template>

<script setup>
import {computed} from "vue";
import HeroVideoVimeo from "@/components/HeroVideoVimeo.vue";
import KeyDates from "@/components/KeyDates";
import RegisterNowWrapper from "@/components/CallToAction/RegisterNowWrapper";
// import HeroImage from "@/components/HeroImage.vue";
import useUserSafe from "@/composables/useUserSafe";
import useLocalEventDates from "@/composables/useLocalEventDates";
import TimeZoneInline from "@/components/TimeZoneInline.vue";
import {requestSubscribe} from "@/composables/useSaveIsSubscribed";
import {getIsSubscribed} from "@/composables/subscriptionLists";
import useOnlineRegisterButton from "@/composables/useOnlineRegisterButton";
import {useRouter} from "vue-router";

const log = (msg) => process.env.VUE_APP_DEV && console.log(); // eslint-disable-line
const router = useRouter();
const {isSignedIn, user} = useUserSafe();
const isSubscribed = computed(() => getIsSubscribed(user.value?.publicMetadata)); // any list
const {unregisteredEventKey, registerLabel, isRegisteredForAll} = useOnlineRegisterButton();
// Profile opens and its mailing list switches turn on in view (SettingsSubscribe.vue)
function openSubscribe() {
  requestSubscribe();
  router.push({hash: "#profile"});
}
// the dates and hours in the viewer's time zone
const {masterclass, workshop, workshopDayHash, hoursRange, upcomingKeyDates} = useLocalEventDates();
// the whole library's size (stats.json "library", every video; Seán, 2026-10-22)

</script>

<style scoped>
/* "(Sydney dates and times ⌄)." on one line */
/* Register Now fills the column's width, as on the Registration page (PostEvent.vue) */
.isFullWidth :deep(a) {
  display: block;
  box-sizing: border-box;
  width: 100%;
}
button[aria-label="Play"],
button[aria-label="Pause"] {
  display: none !important;
}
</style>
